import { TFunction } from "i18next";
import { format } from "date-fns";
import {
  ReservationColumnKey,
  getRoomServiceStatus,
} from "../components/SettlementDataTable";
import { SettlementBooking, PreCheckStatus } from "../types/settlementBooking";
import { RequestStatus, RequestType } from "@/types/request";
import { CheckinStatus, CheckoutStatus } from "@/types/reservation";
import {
  computeBalanceStats,
  formatMoneyFromMinor,
} from "@/lib/billingBalanceStats";

const formatExportDate = (date?: Date | string | null): string => {
  if (!date) {
    return "-";
  }

  return format(new Date(date), "dd.MM.yyyy HH:mm");
};

const getPreCheckStatusLabel = (
  status: PreCheckStatus | undefined,
  t: TFunction,
): string => {
  switch (status) {
    case PreCheckStatus.DONE:
      return t("reservation.precheckin.states.completed");
    case PreCheckStatus.SENT:
      return t("reservation.precheckin.states.sent1Day");
    case PreCheckStatus.SENT_3:
      return t("reservation.precheckin.states.sent3Day");
    case PreCheckStatus.SENT_7:
      return t("reservation.precheckin.states.sent7Day");
    case PreCheckStatus.NOT_SEND:
      return t("reservation.precheckin.states.notSent");
    case PreCheckStatus.NOT_DONE:
    default:
      return t("reservation.precheckin.states.notCompleted");
  }
};

const getRequestsSummary = (booking: SettlementBooking): string => {
  const serviceRequests = booking.serviceRequests ?? [];
  const visibleRequests = serviceRequests.filter(
    (request) => request.type !== RequestType.ROOM,
  );

  if (!visibleRequests.length) {
    return "-";
  }

  const incompleteCount = serviceRequests.filter((request) =>
    request.tasks?.some(
      (task) =>
        task.status !== RequestStatus.DONE &&
        task.status !== RequestStatus.COMPLETED,
    ),
  ).length;

  return `${incompleteCount}/${serviceRequests.length}`;
};

type ExportColumn = {
  header: string;
  getValue: (booking: SettlementBooking) => string;
};

const buildColumns = (
  t: TFunction,
): Record<ReservationColumnKey, ExportColumn | null> => ({
  select: null,
  mainGuest: {
    header: t("reservation.table.guestName"),
    getValue: (booking) =>
      [booking.guest?.profile.firstName, booking.guest?.profile.lastName]
        .filter(Boolean)
        .join(" ") || "-",
  },
  guests: {
    header: t("reservation.table.guests"),
    getValue: (booking) => `${booking.adults ?? 0} / ${booking.children ?? 0}`,
  },
  room: {
    header: t("reservation.table.rooms"),
    getValue: (booking) =>
      booking.room?.number
        ? `${booking.room.number} (${booking.room.category?.name ?? "-"})`
        : "-",
  },
  arrivalDate: {
    header: t("reservation.table.arrival"),
    getValue: (booking) => formatExportDate(booking.arrivalDate),
  },
  departureDate: {
    header: t("reservation.table.departure"),
    getValue: (booking) => formatExportDate(booking.departureDate),
  },
  roomService: {
    header: t("reservation.table.roomService"),
    getValue: (booking) => getRoomServiceStatus(booking) ?? "-",
  },
  requests: {
    header: t("reservation.table.requests"),
    getValue: getRequestsSummary,
  },
  notesCount: {
    header: t("reservation.table.notes"),
    getValue: (booking) => String(booking.notesCount ?? 0),
  },
  reviewsCount: {
    header: t("reservation.table.feedbacks"),
    getValue: (booking) => String(booking.reviewsCount ?? 0),
  },
  balance: {
    header: t("reservation.table.balance"),
    getValue: (booking) => {
      const { balanceMinor, currency } = computeBalanceStats(
        booking.transactions ?? [],
      );

      return formatMoneyFromMinor(balanceMinor, currency);
    },
  },
  preCheckIn: {
    header: t("reservation.table.preCheckIn"),
    getValue: (booking) => getPreCheckStatusLabel(booking.preCheckinStatus, t),
  },
  preCheckinReadAt: {
    header: t("reservation.table.preCheckinReadAt"),
    getValue: (booking) => formatExportDate(booking.preCheckinReadAt),
  },
  preCheckOut: {
    header: t("reservation.table.preCheckOut"),
    getValue: (booking) => getPreCheckStatusLabel(booking.preCheckoutStatus, t),
  },
  checkOutCompleted: {
    header: t("reservation.table.checkOut"),
    getValue: (booking) =>
      booking.checkoutStatus === CheckoutStatus.DONE ? "Confirm" : "Check-out",
  },
  checkInCompleted: {
    header: t("reservation.table.checkIn"),
    getValue: (booking) =>
      booking.checkinStatus === CheckinStatus.DONE ? "Confirm" : "Check-in",
  },
  documentsVerified: {
    header: t("reservation.table.document"),
    getValue: (booking) =>
      (booking.guest?.documents?.every((d) => d.isApproved) ?? false)
        ? "Yes"
        : "No",
  },
  actions: null,
});

const escapeCsvCell = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

export const exportSettlementBookingsToCSV = (
  bookings: SettlementBooking[],
  columnKeys: ReservationColumnKey[],
  t: TFunction,
  filename: string,
): void => {
  if (!bookings.length) {
    return;
  }

  const columns = buildColumns(t);
  const exportColumns = columnKeys
    .map((key) => columns[key])
    .filter((column): column is ExportColumn => column !== null);

  const csvContent = [
    exportColumns.map((column) => escapeCsvCell(column.header)).join(","),
    ...bookings.map((booking) =>
      exportColumns
        .map((column) => escapeCsvCell(column.getValue(booking)))
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
